import { useState } from "react";
import { motion, PanInfo } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingCart, X, Heart } from "lucide-react";
import { Product } from "@/types/common";
import { useOptimizedCart } from "@/contexts/OptimizedCartContext";
import { useToast } from "@/hooks/use-toast";
import { handleProductNavigation } from "@/utils/productUrlUtils";
import { useNavigate } from "react-router-dom";
import { hapticFeedback } from "@/utils/mobileUtils";
import { clsx } from "clsx";

interface SwipeableRecentProductProps {
  product: Product;
  onRemove: (productId: string) => void;
  index: number;
}

const SwipeableRecentProduct = ({ product, onRemove, index }: SwipeableRecentProductProps) => {
  const { addToCart } = useOptimizedCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  const handleDragStart = () => {
    setIsDragging(true);
    setHasTriggered(false);
    hapticFeedback('light');
  };

  const handleDragEnd = (event: any, info: PanInfo) => {
    setIsDragging(false);
    setDragX(0);

    const threshold = 100;
    const velocity = Math.abs(info.velocity.x);
    const offset = info.offset.x;

    // Enhanced gesture detection
    const shouldTrigger = Math.abs(offset) > threshold || velocity > 500;

    if (shouldTrigger && !hasTriggered) {
      setHasTriggered(true);
      hapticFeedback('heavy');
      
      // Swipe right - Add to cart
      if (offset > 0) {
        handleAddToCart();
      }
      // Swipe left - Remove from history
      else {
        handleRemove();
      }
    }
  };

  const handleDrag = (event: any, info: PanInfo) => {
    setDragX(info.offset.x);
    
    // Haptic feedback at threshold
    const progress = Math.abs(info.offset.x) / 100;
    if (progress >= 1 && !hasTriggered) {
      hapticFeedback('medium');
      setHasTriggered(true);
    } else if (progress < 0.5) {
      setHasTriggered(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product);
    toast({
      title: "✨ Added to Cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  const handleRemove = () => {
    onRemove(product.id);
    toast({
      title: "🗑️ Removed from History",
      description: `${product.name} has been removed from recently viewed.`,
    });
  };

  const handleProductClick = () => {
    if (!isDragging && Math.abs(dragX) < 10) {
      handleProductNavigation(product, navigate);
    }
  };

  const getBackgroundColor = () => {
    const progress = Math.abs(dragX) / 100;
    if (dragX > 50) {
      return `rgba(34, 197, 94, ${Math.min(progress * 0.2, 0.2)})`;
    }
    if (dragX < -50) {
      return `rgba(239, 68, 68, ${Math.min(progress * 0.2, 0.2)})`;
    }
    return "transparent";
  };

  const getActionIcon = () => {
    if (dragX > 50) return <ShoppingCart className="h-6 w-6 text-green-600" />;
    if (dragX < -50) return <X className="h-6 w-6 text-red-600" />;
    return null;
  };

  const swipeProgress = Math.min(Math.abs(dragX) / 100, 1);

  return (
    <motion.div
      className="flex-shrink-0 relative"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      style={{
        touchAction: 'pan-x',
        overscrollBehaviorX: 'none'
      }}
    >
      {/* Enhanced Action Indicator */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
        animate={{ 
          opacity: Math.abs(dragX) > 50 ? 1 : 0,
          scale: swipeProgress >= 1 ? 1.1 : 0.8 + (swipeProgress * 0.2)
        }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="p-4 rounded-full shadow-2xl backdrop-blur-sm border-2"
          style={{
            backgroundColor: dragX > 50 ? 'rgba(34, 197, 94, 0.9)' : 'rgba(239, 68, 68, 0.9)',
            borderColor: dragX > 50 ? 'rgb(34 197 94)' : 'rgb(239 68 68)',
            color: 'white'
          }}
          animate={{
            rotate: swipeProgress * 360,
            scale: swipeProgress >= 1 ? [1, 1.2, 1] : 1
          }}
          transition={{ 
            rotate: { duration: 0.3 },
            scale: { duration: 0.3, times: [0, 0.5, 1] }
          }}
        >
          {getActionIcon()}
        </motion.div>
      </motion.div>

      <motion.div
        drag="x"
        dragConstraints={{ left: -150, right: 150 }}
        dragElastic={0.1}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDrag={handleDrag}
        className="cursor-grab active:cursor-grabbing"
        whileDrag={{ scale: 0.95 }}
        style={{
          touchAction: 'pan-x',
          overscrollBehaviorX: 'none'
        }}
      >
        <Card 
          className={clsx(
            'w-64 p-3 transition-all duration-300 cursor-pointer border-2',
            isDragging ? 'shadow-2xl' : 'hover:shadow-lg'
          )}
          onClick={handleProductClick}
          style={{
            backgroundColor: getBackgroundColor(),
            borderColor: swipeProgress >= 1 ? 
              (dragX > 0 ? 'rgb(34 197 94)' : 'rgb(239 68 68)') : 
              'transparent'
          }}
        >
          <div className="flex gap-3">
            <img
              src={product.image_url || "/placeholder.svg"}
              alt={product.name}
              className="w-16 h-16 object-cover rounded-lg"
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm text-foreground line-clamp-2">
                {product.name}
              </h4>
              <p className="text-sm text-muted-foreground mb-2">
                {product.categories?.name}
              </p>
              <div className="flex items-center justify-between">
                <span className="font-bold text-primary">
                  ${product.price}
                </span>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    className="h-7 px-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart();
                    }}
                  >
                    <ShoppingCart className="h-3 w-3" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Enhanced Swipe Progress Indicator */}
      {isDragging && (
        <motion.div
          className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 z-20"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-background/95 backdrop-blur-sm px-4 py-2 rounded-full border shadow-lg">
            <div className="flex items-center gap-2">
              <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full transition-colors duration-200"
                  style={{
                    width: `${Math.min(swipeProgress * 100, 100)}%`,
                    backgroundColor: swipeProgress >= 1 ? 
                      (dragX > 0 ? 'rgb(34 197 94)' : 'rgb(239 68 68)') : 
                      'rgb(59 130 246)'
                  }}
                />
              </div>
              <span className="text-xs text-muted-foreground font-medium">
                {swipeProgress >= 1 ? 'Release!' : 'Keep swiping'}
              </span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Enhanced Swipe Hints */}
      {!isDragging && (
        <motion.div 
          className="absolute -bottom-6 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 1 }}
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="flex gap-3 text-xs text-muted-foreground bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full border">
            <span className="flex items-center gap-1">
              <span className="text-red-500">←</span>
              Remove
            </span>
            <span className="flex items-center gap-1">
              Add
              <span className="text-green-500">→</span>
            </span>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SwipeableRecentProduct;
